<script lang="ts" setup>
import OrderAPI from '~/api/order';

const BLOCK_KEY = 'other-recipient-editing';
const TEST_ID = 'order-recipient-data';

const { t } = useI18n();
const orderStore = useOrderStore();
const userStore = useUserStore();
const modalStore = useModalStore();

const isEditing = ref(false);
const isForOther = ref(false);
const firstName = ref('');
const lastName = ref('');
const timerId = ref<NodeJS.Timeout>();

const phone = usePhoneInput();

const { execute, isValid, parameters } = useRequest(
  OrderAPI.setRecipient,
  () => ({
    body: {
      name: firstName.value,
      lastName: lastName.value,
      phone: phone.value,
    },
  }),
  { validation: () => checkPhoneIsValid(phone.value) },
);

const fullName = computed(
  () =>
    userStore.user &&
    `${userStore.user.name} ${userStore.user.lastName}`.trim(),
);
const formattedPhone = computed(
  () => userStore.user && formatPhone(userStore.user.phone, PhoneMask.detailed),
);
const state = computed(() => {
  if (isEditing.value) return 'editing';
  if (userStore.user) return 'active';
  return 'loading';
});

const onEditingCompleted = () => {
  isEditing.value = !userStore.user?.isPhoneConfirmed;
  orderStore.fetchCurrentOrder();
};

const onUpdateIsForOther = async (v: boolean) => {
  try {
    isForOther.value = v;
    const data = await (v ? execute : OrderAPI.deleteRecipient)();
    if (!data) return;

    orderStore.updateOrderState(data);
    orderStore.deleteBlockKey(BLOCK_KEY);
  } catch {
    modalStore.showError({
      title: t('message.error'),
      action: t('message.action'),
    });
  }
};

watch(
  () => orderStore.otherRecipient,
  (v) => {
    if (v) {
      isForOther.value = true;
      firstName.value = v.name;
      lastName.value = v.lastName;
      phone.value = v.phone;
    }
  },
);

watch(parameters, () => {
  if (
    !isValid.value ||
    (orderStore.otherRecipient &&
      orderStore.otherRecipient.name === firstName.value &&
      orderStore.otherRecipient.lastName === lastName.value &&
      comparePhone(phone.value, orderStore.otherRecipient.phone))
  )
    return;
  orderStore.setBlockKey(BLOCK_KEY);

  clearTimeout(timerId.value);
  timerId.value = setTimeout(() => {
    execute()
      ?.then((data) => {
        orderStore.updateOrderState(data);
        orderStore.deleteBlockKey(BLOCK_KEY);
      })
      .catch(() => {
        modalStore.showError({
          title: t('message.error'),
          action: t('message.action'),
        });
      });
  }, 500);
});
</script>

<template>
  <div class="orderRecipientData" :data-test-id="TEST_ID">
    <div class="orderRecipientData__header">{{ t('title') }}</div>

    <div class="orderRecipientData__content">
      <BaseToggleTransition :state="state">
        <ProfileEditUserForm
          v-if="state === 'editing'"
          @update="onEditingCompleted"
          @cancel="isEditing = false"
        />

        <template v-else-if="state === 'active' && userStore.user">
          <div class="orderRecipientData__info">
            <span>{{ fullName || t('empty_name') }}</span>
            <span>
              {{ formattedPhone || t('empty_phone') }}
              <span
                v-if="!userStore.user.isPhoneConfirmed"
                class="orderRecipientData__info_failed"
              >
                {{ t('unconfirmed') }}
              </span>
            </span>
            <span v-if="userStore.user.email">
              {{ userStore.user.email }}
            </span>
            <span
              v-if="!userStore.user.email"
              class="orderRecipientData__info_failed"
            >
              {{ t('empty_email') }}
            </span>
            <i18n-t
              keypath="hint"
              tag="span"
              class="orderRecipientData__info_additional"
            >
              <template #action>
                <button
                  @click="isEditing = true"
                  class="orderRecipientData__toProfile"
                >
                  {{ t('change') }}
                </button>
              </template>
            </i18n-t>
          </div>

          <label class="orderRecipientData__forOther">
            <BaseCheckbox
              @update:value="onUpdateIsForOther"
              :value="isForOther"
              dark
              size="s"
              class="orderRecipientData__checkbox"
              :data-test-id="`${TEST_ID}-other-toggle`"
            />
            {{ t('other') }}
          </label>

          <BaseCollapse :visible="isForOther">
            <form @submit.prevent class="orderRecipientData__form">
              <BaseTextField
                v-model:value="firstName"
                autocomplete="given-name"
                :placeholder="t('form.first_name_placeholder')"
                bordered
                :data-test-id="`${TEST_ID}-other-first-name`"
              />
              <BaseTextField
                v-model:value="lastName"
                autocomplete="family-name"
                :placeholder="t('form.last_name_placeholder')"
                bordered
                :data-test-id="`${TEST_ID}-other-last-name`"
              />
              <BaseTextField
                :placeholder="t('form.phone_placeholder')"
                bordered
                :data-test-id="`${TEST_ID}-other-phone`"
                v-bind="phone.inputProps"
              />
            </form>
          </BaseCollapse>
        </template>

        <BaseLoader v-else dark area />
      </BaseToggleTransition>
    </div>
  </div>
</template>

<i18n>
ru:
  title: Данные получателя
  empty_name: Имя не указано
  empty_phone: Телефон не указан
  empty_email: Email не указан
  unconfirmed: Не подтвержден
  hint: '{action} личные данные'
  change: Изменить
  other: Заказать для другого
  form:
    first_name_placeholder: Имя
    last_name_placeholder: Фамилия
    phone_placeholder: Телефон
  message:
    error: При сохранении информации о получателе произошла ошибка
    action: Вернуться

en:
  title: Recipient's data
  empty_name: Name not specified
  empty_phone: Phone not specified
  empty_email: Email not specified
  unconfirmed: Not confirmed
  hint: '{action} personal data'
  change: Change
  other: Order for someone else
  form:
    first_name_placeholder: First name
    last_name_placeholder: Last name
    phone_placeholder: Phone
  message:
    error: An error occurred while saving the recipient's information
    action: Return
</i18n>

<style lang="scss">
.orderRecipientData {
  background: #ffffff;
  border-radius: 12px;
  padding: 0 20px;

  &__header {
    height: 64px;
    display: flex;
    align-items: center;
    font-size: 18px;
    line-height: 22px;
    letter-spacing: 0.2px;
    color: #121212;
  }

  &__content {
    border-top: 1px solid rgba(18, 18, 18, 0.2);
    padding: 16px 0 26px;
  }

  &__info {
    display: flex;
    gap: 4px;
    flex-direction: column;
    font-size: 14px;
    line-height: 140%;
    letter-spacing: 0.5px;
    color: #121212;

    &_additional {
      color: rgba(18, 18, 18, 0.5);
    }

    &_failed {
      color: #eb5757;
    }
  }

  &__toProfile {
    position: relative;
    transition: color 0.3s ease;
    color: inherit;
    display: inline-block;
    cursor: pointer;
    padding: 0;
    border: none;
    background: none;

    &:hover {
      color: #121212;
    }

    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      bottom: -2px;
      background-color: currentColor;
    }
  }

  &__forOther {
    letter-spacing: 0.75px;
    color: #121212;
    opacity: 0.9;
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 5px;
    width: fit-content;
    cursor: pointer;
  }

  &__checkbox {
    padding: 2px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 13px 0 6px;
  }
}
</style>
